import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, TextField, MenuItem, CircularProgress,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Chip, IconButton, Tooltip, Button } from '@mui/material';
import { Visibility, CheckCircle, Delete } from '@mui/icons-material';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import ModuleHeader from './components/ModuleHeader';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_URL } from './config';

interface Props { onVoltar: () => void; }

interface Pendencia {
  id: number;
  data_registro: string;
  se: number;
  ciclo_id: number;
  agente_id: number;
  area_id: number;
  tipo_pendencia: string;
  status: string;
  data_recuperacao: string | null;
  observacao: string | null;
}

interface CardAgente { nomes: string[]; qtd: number; }

interface Estatisticas {
  total_registrados: number;
  fechados: number;
  recusados: number;
  pendentes: number;
  recuperados: number;
  saldo_pendente: number;
  agente_mais_pendencias?: CardAgente | null;
  agente_menos_pendencias?: CardAgente | null;
  agente_mais_recuperados?: CardAgente | null;
  ultimo_lancamento_data?: string | null;
  ultimo_lancamento_fechados?: number;
  ultimo_lancamento_recuperados?: number;
}

interface IndicesData {
  totais: { fechados: number; fechados_pendentes: number; fechados_recuperados: number; imoveis_universo: number; };
  indice_pendencia_alfa: { valor: number; status: string; cor: string; descricao_status: string; interpretacao_faixas: string; titulo: string; formula: string; texto_explicativo: string; };
  indice_recuperacao_alfa: { valor: number; titulo: string; formula: string; texto_explicativo: string; };
  indice_pendencia_beta: { valor: number | null; titulo: string; formula: string; texto_explicativo: string; };
  indice_recuperacao_beta: { valor: number | null; titulo: string; formula: string; texto_explicativo: string; };
  velocidade_recuperacao: { media_dias: number | null; mediana_dias: number | null; total_recuperadas_consideradas: number; interpretacao: string; titulo: string; texto_explicativo: string; };
}

interface Ciclo  { id: number; nome: string; ativo: boolean; }
interface Area   { id: number; nome: string; zona: string; }
interface Agente { id: number; nome: string; matricula: string; }

// ─────────────────────────────────────────────────────────────────
export default function Pendencias({ onVoltar }: Props) {
  const { token } = useAuth();

  const [pendencias, setPendencias]     = useState<Pendencia[]>([]);
  const [estatisticas, setEstatisticas] = useState<Estatisticas | null>(null);
  const [indices, setIndices]           = useState<IndicesData | null>(null);
  const [ciclos, setCiclos]             = useState<Ciclo[]>([]);
  const [areas, setAreas]               = useState<Area[]>([]);
  const [agentes, setAgentes]           = useState<Agente[]>([]);
  const [filtroCiclo, setFiltroCiclo]   = useState<string | null>(null);
  const [filtroSE, setFiltroSE]         = useState<string | null>(null);
  const [filtroArea, setFiltroArea]     = useState<string | null>(null);
  const [filtroAgente, setFiltroAgente] = useState<string | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<string | null>(null);
  const [loading, setLoading]           = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/ciclos/`, { headers }),
      axios.get(`${API_URL}/api/areas/`, { headers }),
      axios.get(`${API_URL}/api/agentes/`, { headers }),
    ]).then(([rc, ra, rag]) => {
      setCiclos(rc.data);
      setAreas(ra.data);
      setAgentes(rag.data);
    });
  }, [token]); // eslint-disable-line

  const fetchPendencias = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filtroCiclo)  params.ciclo_id  = filtroCiclo;
      if (filtroSE)     params.se        = filtroSE;
      if (filtroArea)   params.area_id   = filtroArea;
      if (filtroAgente) params.agente_id = filtroAgente;
      if (filtroStatus) params.status    = filtroStatus;
      const { data } = await axios.get(`${API_URL}/api/pendencias/`, { headers, params });
      setPendencias(data);
    } finally {
      setLoading(false);
    }
  }, [filtroCiclo, filtroSE, filtroArea, filtroAgente, filtroStatus, token]); // eslint-disable-line

  const fetchEstatisticas = useCallback(async () => {
    const params: Record<string, string> = {};
    if (filtroCiclo)  params.ciclo_id  = filtroCiclo;
    if (filtroSE)     params.se        = filtroSE;
    if (filtroArea)   params.area_id   = filtroArea;
    if (filtroAgente) params.agente_id = filtroAgente;
    const { data } = await axios.get(`${API_URL}/api/pendencias/estatisticas`, { headers, params });
    setEstatisticas(data);
  }, [filtroCiclo, filtroSE, filtroArea, filtroAgente, token]); // eslint-disable-line

  const fetchIndices = useCallback(async () => {
    const params: Record<string, string> = {};
    if (filtroCiclo)  params.ciclo_id  = filtroCiclo;
    if (filtroSE)     params.se        = filtroSE;
    if (filtroArea)   params.area_id   = filtroArea;
    if (filtroAgente) params.agente_id = filtroAgente;
    const { data } = await axios.get(`${API_URL}/api/pendencias/indices`, { headers, params });
    setIndices(data);
  }, [filtroCiclo, filtroSE, filtroArea, filtroAgente, token]); // eslint-disable-line

  useEffect(() => {
    fetchPendencias();
    fetchEstatisticas();
    fetchIndices();
  }, [fetchPendencias, fetchEstatisticas, fetchIndices]);

  const handleMarcarRecuperado = async (id: number) => {
    await axios.post(`${API_URL}/api/pendencias/${id}/recuperar`, {}, { headers });
    fetchPendencias();
    fetchEstatisticas();
    fetchIndices();
  };

  const handleExcluir = async (id: number) => {
    if (!window.confirm('Excluir esta pendência? Esta ação não pode ser desfeita.')) return;
    await axios.delete(`${API_URL}/api/pendencias/${id}`, { headers });
    fetchPendencias();
    fetchEstatisticas();
    fetchIndices();
  };

  const handleVisualizar = (p: Pendencia) => {
    console.log(p);
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      <ModuleHeader
        titulo="Pendências"
        subtitulo="Imóveis fechados e recusas pendentes de recuperação"
        icone={<AssignmentLateIcon sx={{ fontSize: 32 }} />}
        accentColor="#C62828"
        onVoltar={onVoltar}
        chips={estatisticas ? [
          { label: 'Pendentes',   value: estatisticas.pendentes   ?? 0 },
          { label: 'Recuperados', value: estatisticas.recuperados ?? 0 },
          { label: 'Saldo',       value: estatisticas.saldo_pendente ?? 0 },
        ] : undefined}
      />
      <Box sx={{ px: 2, pt: 2, display: 'flex', justifyContent: 'flex-end', backgroundColor: '#FFFFFF' }}>
        
      </Box>
      <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', backgroundColor: '#FFFFFF', borderBottom: '1px solid #E5E7EB' }}>
        <TextField select label="Ciclo" size="small" sx={{ width: 180 }}
          value={filtroCiclo ?? ''} onChange={e => setFiltroCiclo(e.target.value || null)}>
          <MenuItem value="">Todos</MenuItem>
          {ciclos.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.nome}{c.ativo ? ' ●' : ''}</MenuItem>)}
        </TextField>
        <TextField select label="SE" size="small" sx={{ width: 180 }}
          value={filtroSE ?? ''} onChange={e => setFiltroSE(e.target.value || null)}>
          <MenuItem value="">Todas</MenuItem>
          {Array.from({ length: 53 }, (_, i) => i + 1).map(se => (
            <MenuItem key={se} value={String(se)}>{se}ª</MenuItem>
          ))}
        </TextField>
        <TextField select label="Área" size="small" sx={{ width: 180 }}
          value={filtroArea ?? ''} onChange={e => setFiltroArea(e.target.value || null)}>
          <MenuItem value="">Todas</MenuItem>
          {areas.map(a => <MenuItem key={a.id} value={String(a.id)}>{a.nome}</MenuItem>)}
        </TextField>
        <TextField select label="Agente" size="small" sx={{ width: 180 }}
          value={filtroAgente ?? ''} onChange={e => setFiltroAgente(e.target.value || null)}>
          <MenuItem value="">Todos</MenuItem>
          {agentes.map(a => <MenuItem key={a.id} value={String(a.id)}>{a.nome}</MenuItem>)}
        </TextField>
        <TextField select label="Status" size="small" sx={{ width: 180 }}
          value={filtroStatus ?? ''} onChange={e => setFiltroStatus(e.target.value || null)}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="PENDENTE">PENDENTE</MenuItem>
          <MenuItem value="RECUPERADO">RECUPERADO</MenuItem>
        </TextField>
      </Box>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 2, mb: 3 }}>
          {[
            { label: 'Total Registrados', valor: estatisticas?.total_registrados ?? 0, bg: '#F7F7F8',  color: '#1D1D1F' },
            { label: 'Fechados F',        valor: estatisticas?.fechados           ?? 0, bg: '#FFE4E1',  color: '#C62828' },
            { label: 'Recusados R',       valor: estatisticas?.recusados          ?? 0, bg: '#FFF3E0',  color: '#E65100' },
            { label: 'Pendentes',         valor: estatisticas?.pendentes          ?? 0, bg: '#FFF8E1',  color: '#F57F17' },
            { label: 'Recuperados',       valor: estatisticas?.recuperados        ?? 0, bg: '#E8F5E9',  color: '#2E7D32' },
            { label: 'Saldo Pendente',    valor: estatisticas?.saldo_pendente     ?? 0, bg: '#1D1D1F',  color: '#FFFFFF' },
          ].map(({ label, valor, bg, color }) => (
            <Box key={label} sx={{ p: 2, borderRadius: 2, backgroundColor: bg }}>
              <Typography variant="caption" sx={{ color, opacity: 0.75, display: 'block' }}>{label}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color }}>{valor}</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
          {/* Card Fechados último lançamento */}
          <Box sx={{ p: 2.5, borderRadius: 2, backgroundColor: '#FEF3C7', border: '1.5px solid #FCD34D' }}>
            <Typography sx={{ fontWeight: 600, color: '#92400E', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 10, mb: 0.5 }}>
              Fechados último lanç.
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '2rem', color: '#92400E', lineHeight: 1 }}>
              {estatisticas?.ultimo_lancamento_fechados ?? 0}
            </Typography>
            <Typography sx={{ color: '#78350F', mt: 0.5, fontSize: 10.5, opacity: 0.85 }}>
              {estatisticas?.ultimo_lancamento_data
                ? `em ${new Date(estatisticas.ultimo_lancamento_data + 'T00:00:00').toLocaleDateString('pt-BR')}`
                : 'sem lançamento'}
            </Typography>
          </Box>
          {/* Card Recuperados último lançamento */}
          <Box sx={{ p: 2.5, borderRadius: 2, backgroundColor: '#DBEAFE', border: '1.5px solid #93C5FD' }}>
            <Typography sx={{ fontWeight: 600, color: '#1E40AF', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 10, mb: 0.5 }}>
              Recuperados último lanç.
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '2rem', color: '#1E40AF', lineHeight: 1 }}>
              {estatisticas?.ultimo_lancamento_recuperados ?? 0}
            </Typography>
            <Typography sx={{ color: '#1E3A8A', mt: 0.5, fontSize: 10.5, opacity: 0.85 }}>
              {estatisticas?.ultimo_lancamento_data
                ? `em ${new Date(estatisticas.ultimo_lancamento_data + 'T00:00:00').toLocaleDateString('pt-BR')}`
                : 'sem lançamento'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
          {[
            { label: 'Agente com mais pendências',  campo: estatisticas?.agente_mais_pendencias,  bg: '#FFE4E1', color: '#C62828' },
            { label: 'Agente com menos pendências', campo: estatisticas?.agente_menos_pendencias, bg: '#E8F5E9', color: '#2E7D32' },
            { label: 'Agente com mais recuperados', campo: estatisticas?.agente_mais_recuperados, bg: '#E3F2FD', color: '#1565C0' },
          ].map(({ label, campo, bg, color }) => {
            const nomes: string[] = campo?.nomes ?? [];
            const qtd: number | null = campo?.qtd ?? null;
            return (
              <Box key={label} sx={{ p: 2, bgcolor: bg, borderRadius: 1, display: 'flex', flexDirection: 'column', minHeight: 110 }}>
                <Typography variant="caption" sx={{ color, fontWeight: 600, mb: 1 }}>{label}</Typography>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {nomes.length === 0 ? (
                    <Typography sx={{ color, fontWeight: 700, fontSize: '1.1rem' }}>—</Typography>
                  ) : (
                    nomes.map((n) => (
                      <Typography key={n} sx={{ color, fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2 }}>{n}</Typography>
                    ))
                  )}
                </Box>
                {qtd != null && qtd > 0 && (
                  <Typography sx={{ color, mt: 0.5, fontSize: '0.95rem', opacity: 0.85 }}>({qtd})</Typography>
                )}
              </Box>
            );
          })}
        </Box>
        {indices && (() => {
          const alfaStatus = indices.indice_pendencia_alfa.status;
          const alfaColors: Record<string, { bg: string; border: string; text: string; chipBg: string }> = {
            'EXCELENTE': { bg: '#F0FDF4', border: '#BBF7D0', text: '#166534', chipBg: '#22C55E' },
            'BOM':       { bg: '#EFF6FF', border: '#BFDBFE', text: '#1D4ED8', chipBg: '#3B82F6' },
            'ATENÇÃO':   { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E', chipBg: '#F59E0B' },
            'CRÍTICO':   { bg: '#FFF1F2', border: '#FECDD3', text: '#991B1B', chipBg: '#EF4444' },
          };
          const ac = alfaColors[alfaStatus] ?? alfaColors['CRÍTICO'];

          const vMedia = indices.velocidade_recuperacao.media_dias;
          const velColor = vMedia === null ? '#8E8E93' : vMedia <= 7 ? '#166534' : vMedia <= 14 ? '#92400E' : '#991B1B';
          const velBg    = vMedia === null ? '#F7F7F8' : vMedia <= 7 ? '#F0FDF4' : vMedia <= 14 ? '#FFFBEB' : '#FFF1F2';
          const velBorder= vMedia === null ? '#E5E7EB' : vMedia <= 7 ? '#BBF7D0' : vMedia <= 14 ? '#FDE68A' : '#FECDD3';

          return (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#8E8E93', mb: 1.5, display: 'block' }}>
                Índices de Gestão de Pendências
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>

                {/* Card IP α — Operacional */}
                <Box sx={{ p: 2.5, borderRadius: 2, backgroundColor: ac.bg, border: `1.5px solid ${ac.border}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Tooltip arrow placement="top" title={
                      <Box sx={{ p: 0.5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 12, mb: 0.5, color: '#fff' }}>{indices.indice_pendencia_alfa.titulo}</Typography>
                        <Typography sx={{ fontSize: 10.5, mb: 0.8, color: '#a5b4cb', fontFamily: 'monospace' }}>{indices.indice_pendencia_alfa.formula}</Typography>
                        <Typography sx={{ fontSize: 11.5, lineHeight: 1.5, color: '#e5e7eb' }}>{indices.indice_pendencia_alfa.texto_explicativo}</Typography>
                      </Box>
                    } slotProps={{ tooltip: { sx: { maxWidth: 360, backgroundColor: '#1a2332', p: 1.5 } } }}>
                      <Typography sx={{ fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 10, cursor: 'help', borderBottom: '1px dotted #8E8E93', display: 'inline' }}>
                        IP α — Operacional ⓘ
                      </Typography>
                    </Tooltip>
                    <Chip label={alfaStatus} size="small" sx={{ backgroundColor: ac.chipBg, color: '#fff', fontWeight: 700, fontSize: 10, height: 20 }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '2.1rem', color: ac.text, lineHeight: 1 }}>
                    {indices.indice_pendencia_alfa.valor}%
                  </Typography>
                  <Typography sx={{ color: ac.text, mt: 0.5, fontSize: 12, opacity: 0.8 }}>
                    {indices.indice_pendencia_alfa.descricao_status}
                  </Typography>
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 3 }}>
                    <Box>
                      <Typography sx={{ color: '#8E8E93', fontSize: 10 }}>Pendentes</Typography>
                      <Typography sx={{ fontWeight: 700, color: ac.text, fontSize: '1.1rem' }}>{indices.totais.fechados_pendentes}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#8E8E93', fontSize: 10 }}>Total F</Typography>
                      <Typography sx={{ fontWeight: 700, color: '#1D1D1F', fontSize: '1.1rem' }}>{indices.totais.fechados}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#8E8E93', fontSize: 10 }}>IR α</Typography>
                      <Typography sx={{ fontWeight: 700, color: '#166534', fontSize: '1.1rem' }}>{indices.indice_recuperacao_alfa.valor}%</Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ color: '#8E8E93', mt: 1, fontSize: 10, lineHeight: 1.4 }}>
                    {indices.indice_pendencia_alfa.interpretacao_faixas}
                  </Typography>
                </Box>

                {/* Card IP β — Cobertura */}
                <Box sx={{ p: 2.5, borderRadius: 2, backgroundColor: '#EFF6FF', border: '1.5px solid #BFDBFE' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Tooltip arrow placement="top" title={
                      <Box sx={{ p: 0.5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 12, mb: 0.5, color: '#fff' }}>{indices.indice_pendencia_beta.titulo}</Typography>
                        <Typography sx={{ fontSize: 10.5, mb: 0.8, color: '#a5b4cb', fontFamily: 'monospace' }}>{indices.indice_pendencia_beta.formula}</Typography>
                        <Typography sx={{ fontSize: 11.5, lineHeight: 1.5, color: '#e5e7eb' }}>{indices.indice_pendencia_beta.texto_explicativo}</Typography>
                      </Box>
                    } slotProps={{ tooltip: { sx: { maxWidth: 360, backgroundColor: '#1a2332', p: 1.5 } } }}>
                      <Typography sx={{ fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 10, cursor: 'help', borderBottom: '1px dotted #8E8E93', display: 'inline' }}>
                        IP β — Cobertura ⓘ
                      </Typography>
                    </Tooltip>
                    <Typography sx={{ color: '#1D4ED8', fontWeight: 600, fontSize: 10 }}>
                      {indices.totais.imoveis_universo.toLocaleString('pt-BR')} imóveis
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '2.1rem', color: '#1D4ED8', lineHeight: 1 }}>
                    {indices.indice_pendencia_beta.valor !== null ? `${indices.indice_pendencia_beta.valor}%` : '—'}
                  </Typography>
                  <Typography sx={{ color: '#1D4ED8', mt: 0.5, fontSize: 12, opacity: 0.8 }}>
                    do território ainda sem visita recuperada
                  </Typography>
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 3 }}>
                    <Box>
                      <Typography sx={{ color: '#8E8E93', fontSize: 10 }}>IR β</Typography>
                      <Typography sx={{ fontWeight: 700, color: '#166534', fontSize: '1.1rem' }}>
                        {indices.indice_recuperacao_beta.valor !== null ? `${indices.indice_recuperacao_beta.valor}%` : '—'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#8E8E93', fontSize: 10 }}>Recuperados</Typography>
                      <Typography sx={{ fontWeight: 700, color: '#1D1D1F', fontSize: '1.1rem' }}>{indices.totais.fechados_recuperados}</Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ color: '#8E8E93', mt: 1, fontSize: 10, lineHeight: 1.4 }}>
                    Pendentes ÷ {indices.totais.imoveis_universo.toLocaleString('pt-BR')} imóveis cadastrados
                  </Typography>
                </Box>

                {/* Card Velocidade */}
                <Box sx={{ p: 2.5, borderRadius: 2, backgroundColor: velBg, border: `1.5px solid ${velBorder}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Tooltip arrow placement="top" title={
                      <Box sx={{ p: 0.5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 12, mb: 0.5, color: '#fff' }}>{indices.velocidade_recuperacao.titulo}</Typography>
                        <Typography sx={{ fontSize: 11.5, lineHeight: 1.5, color: '#e5e7eb' }}>{indices.velocidade_recuperacao.texto_explicativo}</Typography>
                      </Box>
                    } slotProps={{ tooltip: { sx: { maxWidth: 360, backgroundColor: '#1a2332', p: 1.5 } } }}>
                      <Typography sx={{ fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 10, cursor: 'help', borderBottom: '1px dotted #8E8E93', display: 'inline' }}>
                        Velocidade de Recuperação ⓘ
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '2.1rem', color: velColor, lineHeight: 1 }}>
                    {vMedia !== null ? `${vMedia} dias` : '—'}
                  </Typography>
                  <Typography sx={{ color: velColor, mt: 0.5, fontSize: 12, opacity: 0.8 }}>
                    média entre registro e recuperação
                  </Typography>
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 3 }}>
                    <Box>
                      <Typography sx={{ color: '#8E8E93', fontSize: 10 }}>Mediana</Typography>
                      <Typography sx={{ fontWeight: 700, color: velColor, fontSize: '1.1rem' }}>
                        {indices.velocidade_recuperacao.mediana_dias !== null ? `${indices.velocidade_recuperacao.mediana_dias} dias` : '—'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#8E8E93', fontSize: 10 }}>Amostras</Typography>
                      <Typography sx={{ fontWeight: 700, color: '#1D1D1F', fontSize: '1.1rem' }}>{indices.velocidade_recuperacao.total_recuperadas_consideradas}</Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ color: '#8E8E93', mt: 1, fontSize: 10, lineHeight: 1.4 }}>
                    {indices.velocidade_recuperacao.interpretacao}
                  </Typography>
                </Box>

              </Box>
            </Box>
          );
        })()}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['#','Data','SE','Ciclo','Agente','Área','Tipo Pend.','Status','Data Recup.','Ações'].map(h => (
                  <TableCell key={h} sx={{
                    backgroundColor: '#1D1D1F',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pendencias.map((p, idx) => {
                const cnome  = ciclos.find(c => c.id === p.ciclo_id)?.nome  ?? '-';
                const anome  = agentes.find(a => a.id === p.agente_id)?.nome ?? '-';
                const arnome = areas.find(a => a.id === p.area_id)?.nome    ?? '-';
                const fmtData = (d: string | null) =>
                  d ? new Date(d + 'T12:00:00').toLocaleDateString('pt-BR') : '-';
                const cells = [
                  { v: idx + 1,                        w: 40  },
                  { v: fmtData(p.data_registro),        bold: true },
                  { v: p.se,                            w: 50  },
                  { v: cnome                                   },
                  { v: anome,                           bold: true },
                  { v: arnome                                  },
                  { v: p.tipo_pendencia                        },
                ];
                const colBg = (i: number) => i % 2 === 0 ? '#FFFFFF' : '#F7F7F8';
                return (
                  <TableRow key={p.id} sx={{ '&:hover .MuiTableCell-root': { filter: 'brightness(0.95)', transition: 'filter 0.15s' } }}>
                    {cells.map((c, i) => (
                      <TableCell key={i} sx={{
                        backgroundColor: colBg(i),
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        ...(c.bold ? { fontWeight: 700, color: '#1D1D1F' } : {}),
                        ...(c.w   ? { width: c.w } : {}),
                      }}>{c.v}</TableCell>
                    ))}
                    <TableCell sx={{ backgroundColor: colBg(7), textAlign: 'center' }}>
                      <Chip label={p.status} size="small" sx={{
                        backgroundColor: p.status === 'RECUPERADO' ? '#34C759' : '#FF9500',
                        color: 'white', fontWeight: 700, fontSize: 10,
                      }} />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: colBg(8), textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {fmtData(p.data_recuperacao)}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: colBg(9), textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <Tooltip title="Visualizar">
                        <IconButton size="small" sx={{ color: '#0071E3' }} onClick={() => handleVisualizar(p)}><Visibility fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Marcar recuperado">
                        <IconButton size="small" sx={{ color: '#34C759' }} onClick={() => handleMarcarRecuperado(p.id)}><CheckCircle fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" sx={{ color: '#FF3B30' }} onClick={() => handleExcluir(p.id)}><Delete fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>}
      </Box>
    </Box>
  );
}
